// Chargen und Mindesthaltbarkeit: Überwachung, Sperre, Freigabe, Rückruf, Rückverfolgung.
import { PERMISSION_COSTS_READ, defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { listInput, listOutput } from '@shared/list'
import { z } from 'zod'
import { zDocumentRef, zLocationRef, zReason, zUnitInfo } from './common'

/** Funktionsschalter „Chargen und MHD“ (Branchenprofil): ohne ihn bietet der Betrieb keine Chargen-Aktionen an */
export const LOTS = 'lots'

const READ = 'inventory.lot.read'
const BLOCK = 'inventory.lot.block'

/** ok = frei · blocked = gesperrt · recall = zurückgerufen · expired = als abgelaufen markiert */
export const LOT_STATUSES = ['ok', 'blocked', 'recall', 'expired'] as const

/** Sichten der Chargenliste */
export const LOT_VIEWS = ['expiring', 'expired', 'blocked', 'recall', 'all'] as const

export const zLotRow = z.object({
  /** `${lotId}:${locationId}` */
  id: z.string(),
  lotId: zId,
  lotNo: z.string(),
  itemId: zId,
  sku: z.string(),
  itemName: z.string(),
  locationId: zId,
  locationCode: z.string(),
  locationName: z.string(),
  bestBefore: z.string().nullable(),
  /** Tage bis zum Datum (negativ = abgelaufen); null ohne Datum */
  daysLeft: z.int().nullable(),
  status: z.enum(LOT_STATUSES),
  statusReason: z.string().nullable(),
  /** Bestand der Charge am Standort in Tausendstel der Basiseinheit */
  qty: z.int(),
  unit: zUnitInfo,
  baseUnit: z.string(),
  /** Wert zum gleitenden Durchschnitt – nur mit „Einstandspreise sehen“ */
  valueCents: z.int().optional(),
})

export const lotList = defineContract({
  name: 'inventory.lot.list',
  title: 'Chargen und MHD anzeigen',
  description: 'Chargen mit Bestand je Standort: bald ablaufend, abgelaufen, gesperrt oder zurückgerufen.',
  llm: {
    description:
      'Listet Chargen mit Bestand je Standort. `view`: „expiring“ = MHD in `withinDays` Tagen erreicht (Standard 30, ' +
      'noch nicht abgelaufen), „expired“ = MHD überschritten, „blocked“ = gesperrt, „recall“ = zurückgerufen, „all“. ' +
      'Mengen in Tausendstel der Basiseinheit, `daysLeft` in Tagen (negativ = abgelaufen). Verwenden für „Was läuft ' +
      'diese Woche ab?“ oder „Welche Chargen sind gesperrt?“. Sperren mit inventory.lot.block, ausbuchen mit einer ' +
      'Korrekturbuchung (inventory.adjustment.saveDraft, Grund Verderb).',
    keywords: ['MHD', 'Mindesthaltbarkeit', 'Charge', 'läuft ab', 'abgelaufen', 'gesperrt', 'Rückruf', 'Sperrbestand'],
  },
  input: listInput({
    sortBy: ['bestBefore', 'itemName', 'qty'],
    defaultSort: { by: 'bestBefore' },
    filters: {
      view: z.enum(LOT_VIEWS).optional(),
      withinDays: z.int().min(0).max(730).optional(),
      locationId: zId.optional(),
      itemId: zId.optional(),
    },
  }),
  output: listOutput(zLotRow),
  permission: READ,
  feature: LOTS,
  risk: 'read',
  scope: 'any',
  fieldPermissions: { 'rows.*.valueCents': PERMISSION_COSTS_READ },
})

export const lotGet = defineContract({
  name: 'inventory.lot.get',
  title: 'Charge anzeigen',
  description: 'Eine Charge mit Bestand je Standort und Lagerplatz und ihrer Rückverfolgung: woher sie kam, wohin sie ging.',
  llm: {
    description:
      'Liefert eine Charge: Nummer, Artikel, MHD, Status mit Grund, Lieferant, Bestand je Standort und Lagerplatz ' +
      '(Tausendstel der Basiseinheit) und die Rückverfolgung – jede Bewegung mit Beleg und Partner. Verwenden bei ' +
      'Rückruf oder Reklamation: „Wohin ging Charge …?“. Die Kennung liefern inventory.lot.list oder inventory.stock.item.',
    keywords: ['Charge', 'Rückverfolgung', 'Rückruf', 'wohin geliefert', 'woher', 'Lieferant'],
  },
  input: z.strictObject({ id: zId }),
  output: z.object({
    id: zId,
    lotNo: z.string(),
    item: z.object({ id: zId, sku: z.string(), name: z.string(), baseUnit: z.string(), unit: zUnitInfo }),
    bestBefore: z.string().nullable(),
    daysLeft: z.int().nullable(),
    producedAt: z.string().nullable(),
    status: z.enum(LOT_STATUSES),
    statusReason: z.string().nullable(),
    supplierName: z.string().nullable(),
    firstReceivedAt: z.date().nullable(),
    /** Bestand an den Standorten, die der Nutzer sehen darf */
    stock: z.array(z.object({ location: zLocationRef, binCode: z.string(), qty: z.int(), blocked: z.boolean() })),
    totalQty: z.int(),
    trace: z.array(
      z.object({
        id: zId,
        at: z.date(),
        businessDate: z.string(),
        movementType: z.string(),
        typeLabel: z.string(),
        qty: z.int(),
        locationName: z.string(),
        isReversal: z.boolean(),
        document: zDocumentRef.nullable(),
        partnerName: z.string().nullable(),
      }),
    ),
    /** darf der Nutzer die Charge sperren, freigeben oder zurückrufen? */
    allowed: z.object({ block: z.boolean(), release: z.boolean(), recall: z.boolean() }),
  }),
  permission: READ,
  feature: LOTS,
  risk: 'read',
  scope: 'any',
})

const zLotStatusResult = z.object({
  lotId: zId,
  lotNo: z.string(),
  status: z.enum(LOT_STATUSES),
  changed: z.boolean(),
  /** Bestand der Charge je Standort, der jetzt gesperrt bzw. wieder frei ist (Tausendstel der Basiseinheit) */
  affected: z.array(z.object({ locationId: zId, locationName: z.string(), qty: z.int() })),
  /** angelegte Aufgaben für die Standorte, die die Charge führen */
  tasks: z.int(),
})

export const lotBlock = defineContract({
  name: 'inventory.lot.block',
  title: 'Charge sperren',
  description: 'Sperrt eine Charge mit Grund: Ihr Bestand ist an allen Standorten nicht mehr verfügbar und wird nicht verkauft.',
  llm: {
    description:
      'Sperrt eine Charge mit Grund. Die Sperre gilt für die ganze Charge an ALLEN Standorten: Der Bestand bleibt ' +
      'liegen, zählt aber nicht mehr als verfügbar und wird weder verkauft noch umgelagert. Andere Standorte mit ' +
      'dieser Charge erhalten eine Aufgabe. Verwenden bei Qualitätsmangel oder abgelaufenem MHD. Nicht zum Ausbuchen ' +
      '(dafür Korrekturbuchung) und nicht für einen Herstellerrückruf (dafür inventory.lot.recall). Rückgängig mit ' +
      'inventory.lot.release.',
    keywords: ['Charge sperren', 'Sperre', 'Qualitätsmangel', 'nicht verkaufen', 'Sperrbestand', 'MHD abgelaufen'],
  },
  input: z.strictObject({ id: zId, reason: zReason, expired: z.boolean().optional() }),
  output: zLotStatusResult,
  permission: BLOCK,
  feature: LOTS,
  risk: 'destructive',
  scope: 'record',
  effects: { reversible: true },
})

export const lotRelease = defineContract({
  name: 'inventory.lot.release',
  title: 'Charge freigeben',
  description: 'Hebt die Sperre einer Charge auf: Ihr Bestand ist wieder verfügbar.',
  llm: {
    description:
      'Gibt eine gesperrte oder zurückgerufene Charge mit Grund wieder frei – ihr Bestand zählt wieder als verfügbar. ' +
      'Verwenden, wenn sich ein Verdacht nicht bestätigt hat. Offene Aufgaben zur Sperre werden erledigt.',
    keywords: ['Charge freigeben', 'Sperre aufheben', 'entsperren', 'wieder verkaufen'],
  },
  input: z.strictObject({ id: zId, reason: zReason }),
  output: zLotStatusResult,
  permission: BLOCK,
  feature: LOTS,
  risk: 'write',
  scope: 'record',
  effects: { reversible: true },
})

export const lotRecall = defineContract({
  name: 'inventory.lot.recall',
  title: 'Charge zurückrufen',
  description: 'Ruft eine Charge zurück: sperrt sie an allen Standorten und legt je Standort eine Aufgabe zum Aussortieren an.',
  llm: {
    description:
      'Rückruf einer Charge (Hersteller, Behörde, eigener Befund): sperrt sie an allen Standorten und legt für jeden ' +
      'Standort, der sie führt, eine Aufgabe „Rückruf“ an. Die Rückverfolgung (inventory.lot.get) zeigt, an wen sie ' +
      'bereits geliefert wurde. Nicht für eine vorsorgliche Sperre ohne Rückruf (dafür inventory.lot.block).',
    keywords: ['Rückruf', 'zurückrufen', 'Charge', 'Hersteller', 'aussortieren', 'Warnung'],
  },
  input: z.strictObject({ id: zId, reason: zReason }),
  output: zLotStatusResult,
  permission: BLOCK,
  feature: LOTS,
  risk: 'destructive',
  scope: 'record',
  effects: { reversible: true },
})
